"use strict";

(function () {
  class SMainCollection extends HTMLElement {
    constructor() {
      super();

      this.NS = "[SMainCollection]";
      this.filterOpener = () => window.PubSub.publish("filter:open");
      this.sortOpener = () => window.PubSub.publish("sort:open");

      this.updateCheckboxLogic = (e) => {
        if (!this.checkboxes || !this.showAllCheckbox) return;

        if (e.target === this.showAllCheckbox) {
          this.checkboxes.forEach((cb) => (cb.checked = cb === this.showAllCheckbox));
        } else {
          this.showAllCheckbox.checked = false;
          const any = Array.from(this.checkboxes).some(
            (cb) => cb !== this.showAllCheckbox && cb.checked
          );
          if (!any) this.showAllCheckbox.checked = true;
        }
      };

      // -------------------------
      // Variant/price patching
      // -------------------------
      this._productCache = new Map(); // handle -> productJson
      this._productInflight = new Map(); // handle -> Promise
      this._patchSeq = 0;
      this._moneySample = null;

      this.captureMoneySampleFromDOM = () => {
        if (this._moneySample) return this._moneySample;

        const el =
          document.querySelector(".c-product-card__price") ||
          document.querySelector("[class*='price']");

        const txt = (el?.textContent || "").trim();
        if (!txt) return null;

        const m = txt.match(/(.*?)(\d[\d.,\s]*\d|\d)(.*)/);
        if (!m) return null;

        const prefix = (m[1] || "").trimEnd();
        const number = (m[2] || "").trim();
        const suffix = (m[3] || "").trimStart();

        let dec = ".";
        const lastDot = number.lastIndexOf(".");
        const lastComma = number.lastIndexOf(",");
        if (lastComma > lastDot) dec = ",";
        if (lastDot > lastComma) dec = ".";
        if (lastDot === -1 && lastComma !== -1) dec = ",";

        this._moneySample = { prefix, suffix, dec };
        return this._moneySample;
      };

      this.formatMoney = (cents) => {
        const sample = this.captureMoneySampleFromDOM();
        const n = Number(cents);
        if (!Number.isFinite(n)) return "";

        let value = (n / 100).toFixed(2);
        if (sample?.dec === ",") value = value.replace(".", ",");

        const pre = sample?.prefix ? sample.prefix + " " : "";
        const suf = sample?.suffix ? " " + sample.suffix : "";
        return `${pre}${value}${suf}`.replace(/\s+/g, " ").trim();
      };

      this._makeSearchParams = (searchSource) => {
        try {
          if (!searchSource) return new URLSearchParams(window.location.search);

          const src = String(searchSource).trim();

          // full URL
          if (src.includes("http://") || src.includes("https://")) {
            return new URL(src, window.location.origin).searchParams;
          }

          // "/path?...":
          if (src.includes("?")) {
            return new URL(src, window.location.origin).searchParams;
          }

          // "?a=b&c=d"
          if (src.startsWith("?")) {
            return new URLSearchParams(src);
          }

          // raw query string: "a=b&c=d"
          if (src.includes("=")) {
            return new URLSearchParams("?" + src);
          }

          return new URLSearchParams(window.location.search);
        } catch {
          return new URLSearchParams(window.location.search);
        }
      };

      // Map taxonomy IDs -> size key
      this.TAXONOMY_SIZE_MAP = {
        "2912": { sizeKey: "extra_small", optionValues: ["Extra Small", "XS", "X-Small"] },
        "2913": { sizeKey: "small", optionValues: ["Small", "S"] },
        "2914": { sizeKey: "medium", optionValues: ["Medium", "M"] },
        "2915": { sizeKey: "large", optionValues: ["Large", "L"] },
        "2916": { sizeKey: "extra_large", optionValues: ["Extra Large", "XL", "X-Large"] },
      };

      this.getActiveSizeFromUrl = (searchSource) => {
        const p = this._makeSearchParams(searchSource);

        // (A) option value filter (checkbox)
        const labelVals = p.getAll("filter.v.option.size").filter((x) => String(x || "").trim());
        if (labelVals.length) {
          const raw = String(labelVals[0] || "").trim();
          const key = raw.toLowerCase().trim();

          if (key === "small" || key === "s") return { sizeKey: "small", optionValues: ["Small", "S"] };
          if (key === "medium" || key === "m") return { sizeKey: "medium", optionValues: ["Medium", "M"] };
          if (key === "large" || key === "l") return { sizeKey: "large", optionValues: ["Large", "L"] };
          if (key.includes("extra") && key.includes("small"))
            return { sizeKey: "extra_small", optionValues: ["Extra Small", "XS", "X-Small"] };
          if (key.includes("extra") && key.includes("large"))
            return { sizeKey: "extra_large", optionValues: ["Extra Large", "XL", "X-Large"] };

          return { sizeKey: key, optionValues: [raw] };
        }

        // (B) taxonomy filter (Woopf + Purrz)
        const gidVals = []
          .concat(p.getAll("filter.v.t.shopify.accessory-size"))     // Woopf
          .concat(p.getAll("filter.v.t.custom.accessory_size"))      // Purrz ✅
          .concat(p.getAll("filter.v.t.custom.accessory-size"))
          .concat(p.getAll("filter.v.t.shopify.accessory_size"))
          .filter((x) => String(x || "").trim());

        if (!gidVals.length) return null;

        const gid = String(gidVals[0] || "");
        const extractId = (s) => {
          const m = String(s || "").match(/TaxonomyValue\/(\d+)/);
          return m?.[1] || "";
        };

        let id = extractId(gid);
        if (!id) {
          try {
            id = extractId(decodeURIComponent(gid));
          } catch {}
        }

        if (id && this.TAXONOMY_SIZE_MAP[id]) return this.TAXONOMY_SIZE_MAP[id];
        return null;
      };

      this.fetchProductJsonByHandle = async (handle) => {
        if (!handle) return null;
        if (this._productCache.has(handle)) return this._productCache.get(handle);
        if (this._productInflight.has(handle)) return this._productInflight.get(handle);

        const req = fetch(`/products/${handle}.js`, { credentials: "same-origin" })
          .then((r) => (r.ok ? r.json() : null))
          .then((json) => {
            if (json) this._productCache.set(handle, json);
            return json;
          })
          .catch(() => null)
          .finally(() => this._productInflight.delete(handle));

        this._productInflight.set(handle, req);
        return req;
      };

      this.findSizeOptionIndex = (productJson) => {
        const opts = Array.isArray(productJson?.options) ? productJson.options : [];
        if (!opts.length) return -1;

        let idx = opts.findIndex((n) => /size/i.test(String(n || "")));
        if (idx >= 0) return idx;

        idx = opts.findIndex((n) => /dimension|length|width/i.test(String(n || "")));
        return idx;
      };

      this.pickVariantForSize = (productJson, optionValues) => {
        const variants = Array.isArray(productJson?.variants) ? productJson.variants : [];
        if (!variants.length) return null;

        const sizeOptIdx = this.findSizeOptionIndex(productJson);

        const norm = (s) =>
          String(s || "")
            .toLowerCase()
            .trim()
            .replace(/\u00a0/g, " ")
            .replace(/\s+/g, " ");

        const desired = (optionValues || []).map(norm);

        if (sizeOptIdx >= 0) {
          const exact = variants.find((v) => desired.includes(norm(v?.options?.[sizeOptIdx])));
          if (exact) return exact;

          const partial = variants.find((v) => {
            const val = norm(v?.options?.[sizeOptIdx]);
            return desired.some((d) => val === d || val.includes(d) || d.includes(val));
          });
          if (partial) return partial;
        }

        const any = variants.find((v) => {
          const opts = Array.isArray(v?.options) ? v.options : [];
          return opts.some((o) => desired.includes(norm(o)));
        });
        if (any) return any;

        return null;
      };

      this.buildVariantUrl = (baseHref, variantId) => {
        try {
          const u = new URL(baseHref || "/", window.location.origin);
          u.searchParams.set("variant", String(variantId));
          return u.toString();
        } catch {
          return `${baseHref}?variant=${variantId}`;
        }
      };

      this.freezeCard = (cardEl) => {
        if (!cardEl) return;
        if (cardEl.dataset.freeze === "1") return;
        cardEl.dataset.freeze = "1";

        const pricing = cardEl.querySelector(".c-product-card__pricing");
        if (pricing) {
          pricing.dataset.prevVisibility = pricing.style.visibility || "";
          pricing.style.visibility = "hidden";
        }

        const links = cardEl.querySelectorAll('a[href*="/products/"]');
        links.forEach((a) => {
          a.dataset.prevPointer = a.style.pointerEvents || "";
          a.style.pointerEvents = "none";
        });
      };

      this.unfreezeCard = (cardEl) => {
        if (!cardEl) return;

        const pricing = cardEl.querySelector(".c-product-card__pricing");
        if (pricing) {
          pricing.style.visibility = pricing.dataset.prevVisibility || "";
          delete pricing.dataset.prevVisibility;
        }

        const links = cardEl.querySelectorAll('a[href*="/products/"]');
        links.forEach((a) => {
          a.style.pointerEvents = a.dataset.prevPointer || "";
          delete a.dataset.prevPointer;
        });
      };

      this.patchCardToVariant = (cardEl, variant, handle) => {
        if (!cardEl || !variant?.id) return;

        const variantId = String(variant.id);

        // these map to your Liquid attributes (data-variant-id / data-variant-url)
        cardEl.dataset.variantId = variantId;
        cardEl.dataset.variantUrl = `/products/${handle}?variant=${variantId}`;

        // update links
        const anchors = cardEl.querySelectorAll('a[href*="/products/"]');
        anchors.forEach((a) => (a.href = this.buildVariantUrl(a.href, variantId)));

        // update pricing UI
        const priceRoot = cardEl.querySelector(".c-product-card__pricing");
        if (!priceRoot) return;

        const price = this.formatMoney(variant.price);
        const hasCompare =
          variant.compare_at_price && Number(variant.compare_at_price) > Number(variant.price);
        const compare = hasCompare ? this.formatMoney(variant.compare_at_price) : "";

        const priceNew = priceRoot.querySelector(".c-product-card__price--new");
        const priceCompare = priceRoot.querySelector(".c-product-card__price--compare-at-price");
        const priceSingle = priceRoot.querySelector(
          ".c-product-card__price:not(.c-product-card__price--new):not(.c-product-card__price--compare-at-price)"
        );

        if (hasCompare) {
          if (priceNew) priceNew.textContent = price;
          if (priceCompare) {
            priceCompare.textContent = compare;
            priceCompare.style.display = "";
          }
          if (priceSingle) priceSingle.textContent = price;
        } else {
          if (priceSingle) priceSingle.textContent = price;
          if (priceNew) priceNew.textContent = price;
          if (priceCompare) priceCompare.style.display = "none";
        }
      };

      this.applyActiveSizeVariantToCards = async (searchSource) => {
        const active = this.getActiveSizeFromUrl(searchSource);
        if (!active) return;

        const seq = ++this._patchSeq;

        const cards = Array.from(this.querySelectorAll("c-product-card[data-product-handle]"));
        if (!cards.length) return;

        cards.forEach((c) => {
          if (c.dataset.patchedSize === active.sizeKey) return;
          this.freezeCard(c);
        });

        const CONCURRENCY = 6;
        let idx = 0;

        const worker = async () => {
          while (idx < cards.length) {
            const i = idx++;
            const card = cards[i];
            if (seq !== this._patchSeq) return;

            if (card.dataset.patchedSize === active.sizeKey) {
              this.unfreezeCard(card);
              continue;
            }

            const handle = String(card.dataset.productHandle || "").trim();
            if (!handle) {
              this.unfreezeCard(card);
              continue;
            }

            const productJson = await this.fetchProductJsonByHandle(handle);
            if (!productJson) {
              this.unfreezeCard(card);
              continue;
            }

            const variant = this.pickVariantForSize(productJson, active.optionValues);
            if (!variant) {
              this.unfreezeCard(card);
              continue;
            }

            this.patchCardToVariant(card, variant, handle);
            card.dataset.patchedSize = active.sizeKey;
            this.unfreezeCard(card);
          }
        };

        const runners = Array.from({ length: Math.min(CONCURRENCY, cards.length) }, () => worker());
        await Promise.all(runners);
      };

      // -------------------------
      // Rendering
      // -------------------------
      this.renderPage = (url, append = false, push = true) => {
        if (!url) return Promise.resolve();

        return fetch(url)
          .then((r) => r.text())
          .then((html) => {
            const wrap = document.createElement("div");
            wrap.innerHTML = html;

            if (append) {
              Array.from(wrap.querySelectorAll(".js-main-collection__item")).forEach((el) => {
                this.list.appendChild(el);
              });

              const nextBtn = wrap.querySelector(".js-main-collection__load_more-bnt");
              if (nextBtn) {
                this.LoadMoreBtn.dataset.link = nextBtn.dataset.link;
                this.LoadMoreBtn.innerHTML = nextBtn.innerHTML;
              } else if (this.LoadMoreBtn) {
                // Last page reached: drop the stale link so the button cannot
                // re-append the page it just added.
                delete this.LoadMoreBtn.dataset.link;
                this.LoadMoreBtn.style.display = "none";
              }

              const nextSort = wrap.querySelector(".js-main-collection__sort span");
              if (nextSort && this.sortBtnText) this.sortBtnText.innerHTML = nextSort.innerHTML;

              const nextCount = wrap.querySelector(".js-main-collection__count");
              if (nextCount && this.LoadMoreCount) {
                this.LoadMoreCount.innerHTML = nextCount.innerHTML;
                // Appending a page moves the "shown" number, so the cached
                // server line has to go with it or clearing a filter would
                // restore the count from before the append.
                delete this.LoadMoreCount.dataset.serverText;
                this.LoadMoreCount.dataset.total = nextCount.dataset.total;
                this.LoadMoreCount.dataset.countTemplate = nextCount.dataset.countTemplate;
              }

              if (this.toolbar) this.applyClientFilters();
            } else {
              const nextList = wrap.querySelector(".js-main-collection__list");
              if (nextList) this.list.innerHTML = nextList.innerHTML;

              const nextBtn = wrap.querySelector(".js-main-collection__load_more-bnt");
              if (nextBtn) {
                this.LoadMoreBtn.dataset.link = nextBtn.dataset.link;
                this.LoadMoreBtn.innerHTML = nextBtn.innerHTML;
                this.LoadMoreBtn.style.display = "";
              } else if (this.LoadMoreBtn) {
                this.LoadMoreBtn.style.display = "none";
              }

              // In toolbar mode there is no legacy sort button to update, but a
              // response can still carry one; writing to a null node here used
              // to throw and abandon the rest of this branch, leaving the
              // count, the toolbar and the URL on their previous values.
              const nextSort = wrap.querySelector(".js-main-collection__sort span");
              if (nextSort && this.sortBtnText) this.sortBtnText.innerHTML = nextSort.innerHTML;

              const nextCount = wrap.querySelector(".js-main-collection__count");
              if (nextCount && this.LoadMoreCount) {
                this.LoadMoreCount.innerHTML = nextCount.innerHTML;
                delete this.LoadMoreCount.dataset.serverText;
                this.LoadMoreCount.dataset.total = nextCount.dataset.total;
                this.LoadMoreCount.dataset.countTemplate = nextCount.dataset.countTemplate;
              }

              // The toolbar is server-rendered, so its counts, active values
              // and disabled options come back correct with the new page.
              this.replaceToolbar(wrap);

              const nextEmpty = wrap.querySelector(".s-main-collection__no-results");
              const currentEmpty = this.querySelector(".s-main-collection__no-results");
              if (currentEmpty) currentEmpty.remove();
              if (nextEmpty) this.list.insertAdjacentElement("afterend", nextEmpty);

              if (push) window.history.pushState({ path: url }, "", url);
            }

            window.PubSub.publish("product-card-image:load");

            // ✅ ALWAYS patch after render, using the SAME url that was rendered
            this.applyActiveSizeVariantToCards(url);
          })
          .catch((e) => console.error("Request Error:", e));
      };

      this.LoadMore = () => {
        const link = this.LoadMoreBtn?.dataset?.link;
        if (!link) return;
        this.renderPage(link, true);
      };

      // Filters (unchanged)
      this.filterHandler = () => {
        const urlParams = new URLSearchParams(window.location.search);
        const existing = {};
        urlParams.forEach((v, k) => {
          existing[k] = existing[k] ? [].concat(existing[k], v) : v;
        });

        const next = {};
        this.checkboxes.forEach((cb) => {
          if (!next[cb.name]) next[cb.name] = [];
          if (cb.checked) next[cb.name].push(cb.value);
        });

        const merged = Object.assign({}, existing, next);

        if (
          !this.showAllCheckbox.checked &&
          merged["filter.v.option.size"] &&
          merged["filter.v.option.size"].length !== 0
        ) {
          // keep
        } else {
          delete merged["filter.v.option.size"];
        }

        const out = new URLSearchParams();
        Object.entries(merged).forEach(([k, v]) => {
          if (Array.isArray(v)) v.forEach((x) => out.append(k, x));
          else out.append(k, v);
        });

        const url = `${window.location.pathname}${out.toString() ? `?${out.toString()}` : ""}`;
        this.renderPage(url);
      };

      // -------------------------
      // v3 toolbar
      // -------------------------

      this.replaceToolbar = (wrap) => {
        const nextToolbar = wrap.querySelector(".js-collection-toolbar");
        if (!nextToolbar || !this.toolbar) return;

        // Carry the client-side control values across the swap: they are not
        // part of the URL, so a re-render would otherwise silently reset them.
        const carried = this.readClientFilters();

        this.toolbar.innerHTML = nextToolbar.innerHTML;
        this.cacheToolbarNodes();

        if (this.searchInput) this.searchInput.value = carried.term;
        if (this.maxPriceInput) this.maxPriceInput.value = carried.maxPriceRaw;
        if (this.saleOnlyInput) this.saleOnlyInput.checked = carried.saleOnly;
        if (this.reviewedOnlyInput) this.reviewedOnlyInput.checked = carried.reviewedOnly;
        if (carried.extrasOpen) this.setExtrasOpen(true);

        this.applyClientFilters();
      };

      this.cacheToolbarNodes = () => {
        if (!this.toolbar) return;
        this.searchInput = this.toolbar.querySelector(".js-collection-search");
        this.sortSelect = this.toolbar.querySelector(".js-collection-sort");
        this.moreBtn = this.toolbar.querySelector(".js-collection-more");
        this.extras = this.toolbar.querySelector("#collection-extras");
        this.maxPriceInput = this.toolbar.querySelector(".js-collection-max-price");
        this.saleOnlyInput = this.toolbar.querySelector(".js-collection-sale-only");
        this.reviewedOnlyInput = this.toolbar.querySelector(".js-collection-reviewed-only");
        this.resultsEl = this.toolbar.querySelector(".js-collection-results");
        this.resultsTotalEl = this.resultsEl ? this.resultsEl.querySelector("b") : null;
        if (this.resultsTotalEl && this.resultsTotalEl.dataset.total === undefined) {
          this.resultsTotalEl.dataset.total = this.resultsTotalEl.textContent.trim();
        }
      };

      this.readClientFilters = () => {
        const maxPriceRaw = this.maxPriceInput ? this.maxPriceInput.value : "";
        const maxPrice = parseFloat(maxPriceRaw);
        return {
          term: this.searchInput ? this.searchInput.value.trim().toLowerCase() : "",
          maxPriceRaw,
          maxPrice: Number.isFinite(maxPrice) ? maxPrice * 100 : null,
          saleOnly: this.saleOnlyInput ? this.saleOnlyInput.checked : false,
          reviewedOnly: this.reviewedOnlyInput ? this.reviewedOnlyInput.checked : false,
          extrasOpen: this.extras ? !this.extras.hidden : false,
        };
      };

      // Narrows the products already on the page. Deliberately separate from
      // the URL filters: these cannot promise a total across pages, so they
      // never touch the address bar.
      this.applyClientFilters = () => {
        if (!this.list) return;

        const f = this.readClientFilters();
        const isActive = f.term !== "" || f.maxPrice !== null || f.saleOnly || f.reviewedOnly;

        if (isActive && !this._autoLoading && this.LoadMoreBtn?.dataset?.link) {
          this.loadAllRemaining();
        }

        const items = Array.from(this.list.querySelectorAll(".js-main-collection__item"));

        let shown = 0;

        items.forEach((item) => {
          // Promo tiles carry no product data; they stay put unless the
          // shopper is actively narrowing the grid.
          const isProduct = item.dataset.title !== undefined;

          if (!isProduct) {
            item.hidden = isActive;
            return;
          }

          const title = item.dataset.title || "";
          const price = Number(item.dataset.price);
          const onSale = item.dataset.onSale === "true";
          const reviews = Number(item.dataset.reviews) || 0;

          let visible = true;
          if (f.term && !title.includes(f.term)) visible = false;
          if (visible && f.maxPrice !== null && Number.isFinite(price) && price > f.maxPrice) visible = false;
          if (visible && f.saleOnly && !onSale) visible = false;
          if (visible && f.reviewedOnly && reviews < 1) visible = false;

          item.hidden = !visible;
          if (visible) shown += 1;
        });

        if (this.resultsTotalEl) {
          const n = isActive ? shown : Number(this.resultsTotalEl.dataset.total);
          this.resultsTotalEl.textContent = String(n);

          const nounEl = this.resultsEl?.querySelector(".js-collection-results-noun");
          if (nounEl && this.resultsEl) {
            const one = this.resultsEl.dataset.nounOne;
            const many = this.resultsEl.dataset.nounMany;
            if (one && many) nounEl.textContent = n === 1 ? one : many;
          }
        }

        // The pagination line is server-rendered as "shown of total" and knows
        // nothing about a client filter, so it kept reporting the page size.
        // It is rewritten from the same template the server used, which keeps
        // it correct in every locale.
        const countEl = this.querySelector(".js-main-collection__count");
        if (countEl) {
          if (countEl.dataset.serverText === undefined) {
            countEl.dataset.serverText = countEl.textContent.trim();
          }
          const template = countEl.dataset.countTemplate;
          const total = countEl.dataset.total;
          countEl.textContent =
            isActive && template
              ? template.replace("__C__", String(shown)).replace("__T__", total)
              : countEl.dataset.serverText;
        }

        // Loading another page while a client filter is on would append
        // products the filter then hides, so the button waits.
        if (isActive && this.LoadMoreBtn) this.LoadMoreBtn.style.display = "none";

        this.toggleClientEmptyState(isActive && shown === 0);
      };

      // Search and the extras only narrow what is in the DOM, so before the
      // first of them takes effect the remaining pages are pulled in. Without
      // this the result count would quietly report a total for one page.
      this.loadAllRemaining = async () => {
        if (this._autoLoading) return;
        this._autoLoading = true;
        try {
          let guard = 0;
          while (this.LoadMoreBtn?.dataset?.link && guard < 6) {
            await this.renderPage(this.LoadMoreBtn.dataset.link, true);
            guard += 1;
          }
        } finally {
          this._autoLoading = false;
        }
        this.applyClientFilters();
      };

      this.toggleClientEmptyState = (show) => {
        let el = this.querySelector(".js-collection-client-empty");

        if (!show) {
          if (el) el.remove();
          return;
        }
        if (el || !this.list) return;

        el = document.createElement("div");
        el.className = "s-main-collection__no-results js-collection-client-empty";
        el.innerHTML = `<p>${this.toolbar?.dataset.noResults || "No products match those filters."}</p>`;
        this.list.insertAdjacentElement("afterend", el);
      };

      this.setExtrasOpen = (open) => {
        if (!this.extras || !this.moreBtn) return;
        this.extras.hidden = !open;
        this.moreBtn.setAttribute("aria-expanded", String(open));
      };

      // Rebuilds the collection URL from the toolbar's checked filter values.
      this.buildToolbarUrl = () => {
        const base = this.toolbar?.dataset.collectionUrl || window.location.pathname;
        const params = new URLSearchParams();

        // Carry over anything the toolbar does not own, such as ?view=, so a
        // filter click cannot drop the caller off an alternate template.
        new URLSearchParams(window.location.search).forEach((value, key) => {
          if (key.startsWith("filter.") || key === "sort_by") return;
          params.append(key, value);
        });

        this.toolbar.querySelectorAll(".js-collection-dropdown input:checked").forEach((input) => {
          params.append(input.name, input.value);
        });

        // Sort rides in on the same checked-input sweep as the filters. The
        // default sort is what the collection already does, so dropping it
        // keeps the shared URL clean.
        const defaultSort = this.toolbar?.dataset.defaultSort || "";
        if (defaultSort && params.get("sort_by") === defaultSort) params.delete("sort_by");

        const qs = params.toString();
        return qs ? `${base}?${qs}` : base;
      };

      this.onToolbarChange = (e) => {
        const target = e.target;
        if (!target) return;

        if (target.closest(".js-collection-dropdown")) {
          if (target.type === "radio") this.closeDropdowns();
          this.renderPage(this.buildToolbarUrl());
          return;
        }

        if (
          target.classList.contains("js-collection-max-price") ||
          target.classList.contains("js-collection-sale-only") ||
          target.classList.contains("js-collection-reviewed-only")
        ) {
          this.applyClientFilters();
        }
      };

      this.onToolbarInput = (e) => {
        const target = e.target;
        if (!target) return;
        if (
          target.classList.contains("js-collection-search") ||
          target.classList.contains("js-collection-max-price")
        ) {
          clearTimeout(this._clientFilterTimer);
          this._clientFilterTimer = setTimeout(this.applyClientFilters, 150);
        }
      };

      this.onToolbarClick = (e) => {
        const toggle = e.target.closest(".js-collection-dropdown-toggle");
        if (toggle) {
          const menu = toggle.nextElementSibling;
          const open = toggle.getAttribute("aria-expanded") === "true";
          this.closeDropdowns();
          if (!open) {
            toggle.setAttribute("aria-expanded", "true");
            if (menu) menu.hidden = false;
          }
          return;
        }

        if (e.target.closest(".js-collection-more")) {
          this.setExtrasOpen(this.extras ? this.extras.hidden : false);
          return;
        }

        // A click anywhere else inside an open menu must not close it.
        if (!e.target.closest(".s-main-collection__dropdown-menu")) {
          this.closeDropdowns();
        }
      };

      this.closeDropdowns = () => {
        if (!this.toolbar) return;
        this.toolbar.querySelectorAll(".js-collection-dropdown-toggle").forEach((btn) => {
          btn.setAttribute("aria-expanded", "false");
          const menu = btn.nextElementSibling;
          if (menu) menu.hidden = true;
        });
      };

      this.onDocumentClick = (e) => {
        if (this.toolbar && !this.toolbar.contains(e.target)) this.closeDropdowns();
      };

      this.onDocumentKeydown = (e) => {
        if (e.key === "Escape") this.closeDropdowns();
      };

      this.onToolbarClear = (e) => {
        const clear = e.target.closest(".js-collection-clear");
        if (!clear) return;
        e.preventDefault();

        if (this.searchInput) this.searchInput.value = "";
        if (this.maxPriceInput) this.maxPriceInput.value = "";
        if (this.saleOnlyInput) this.saleOnlyInput.checked = false;
        if (this.reviewedOnlyInput) this.reviewedOnlyInput.checked = false;

        this.renderPage(clear.getAttribute("href"));
      };
    }

    connectedCallback() {
      if (this.querySelector(".js-main-collection__filter-form")) {
        this.form = this.querySelector(".js-main-collection__filter-form") || null;
        this.checkboxes =
          Array.from(this.form?.querySelectorAll("input[name='filter.v.option.size']")) || [];
        this.showAllCheckbox = this.checkboxes?.[0] || null;
      }

      this.LoadMoreCount = this.querySelector(".s-main-collection__load_more span");
      this.LoadMoreBtn = this.querySelector(".js-main-collection__load_more-bnt");
      this.list = this.querySelector(".js-main-collection__list");
      this.filterBtn = this.querySelector(".js-main-collection__filter");
      this.sortBtn = this.querySelector(".js-main-collection__sort");
      this.sortBtnText = this.querySelector(".js-main-collection__sort span");

      this.toolbar = this.querySelector(".js-collection-toolbar");
      if (this.toolbar) {
        this.cacheToolbarNodes();
        this.toolbar.addEventListener("change", this.onToolbarChange);
        this.toolbar.addEventListener("input", this.onToolbarInput);
        this.toolbar.addEventListener("click", this.onToolbarClick);
        this.toolbar.addEventListener("click", this.onToolbarClear);
        document.addEventListener("click", this.onDocumentClick);
        document.addEventListener("keydown", this.onDocumentKeydown);
      }

      this.LoadMoreBtn && this.LoadMoreBtn.addEventListener("click", this.LoadMore);
      this.filterBtn && this.filterBtn.addEventListener("click", this.filterOpener);
      this.sortBtn && this.sortBtn.addEventListener("click", this.sortOpener);

      this.form && this.form.addEventListener("change", this.filterHandler);
      this.checkboxes &&
        this.checkboxes.forEach((cb) => cb.addEventListener("change", this.updateCheckboxLogic));

      // If something else publishes "collection:render", we still patch based on that URL
      window.PubSub.subscribe("collection:render", (url) => {
        this.renderPage(url);
      });

      // ✅ patch initial render
      this.applyActiveSizeVariantToCards(window.location.href);

      // ✅ patch on back/forward navigation
      this._onPopState = () => {
        if (this.toolbar) {
          this.renderPage(window.location.href, false, false);
          return;
        }
        this.applyActiveSizeVariantToCards(window.location.href);
      };
      window.addEventListener("popstate", this._onPopState);
    }

    disconnectedCallback() {
      this.form && this.form.removeEventListener("change", this.filterHandler);
      this.checkboxes &&
        this.checkboxes.forEach((cb) => cb.removeEventListener("change", this.updateCheckboxLogic));
      this.LoadMoreBtn && this.LoadMoreBtn.removeEventListener("click", this.LoadMore);
      this.filterBtn && this.filterBtn.removeEventListener("click", this.filterOpener);
      this.sortBtn && this.sortBtn.removeEventListener("click", this.sortOpener);
      this._onPopState && window.removeEventListener("popstate", this._onPopState);

      if (this.toolbar) {
        this.toolbar.removeEventListener("change", this.onToolbarChange);
        this.toolbar.removeEventListener("input", this.onToolbarInput);
        this.toolbar.removeEventListener("click", this.onToolbarClick);
        this.toolbar.removeEventListener("click", this.onToolbarClear);
        document.removeEventListener("click", this.onDocumentClick);
        document.removeEventListener("keydown", this.onDocumentKeydown);
      }
      clearTimeout(this._clientFilterTimer);
    }
  }

  if (!customElements.get("s-main-collection")) {
    customElements.define("s-main-collection", SMainCollection);
  }
})();
